import type {State} from 'src/reducers';
import type {Contact, ContactList, ContactMap} from 'src/types/contacts';

import {createSelector} from 'reselect';
import sortBy from 'lodash/sortBy';

import {captureSentryMessage} from 'src/utils/sentry';


const useParam = (state: State, param: string) => param;

export const getContacts = (state: State): ContactMap =>
  state.contacts.contacts;
export const selectContactsAsArray: (arg1: State) => Contact[] = createSelector(
  getContacts,
  (contacts) => Object.values(contacts),
);

export const selectContact = (
  state: State,
  contactId: string,
): Contact | null | undefined => state.contacts.contacts[contactId];

export const getContactByPhone = (
  contacts: Contact[],
  phone: string,
): Contact | null | undefined =>
  contacts.find((contact) => {
    checkForContactPhoneError(contact);
    return contact.phoneNumbers.some(({value}) => value === phone);
  });

export const selectContactByPhone: (
  arg1: State,
  arg2: string,
) => Contact | null | undefined = createSelector(
  selectContactsAsArray,
  useParam,
  getContactByPhone,
);

const getEditingList = (state: State) =>
  // @ts-expect-error - TS2339 - Property 'editing' does not exist on type 'ContactListsState'.
  state.contactLists.editing.contacts;
export const selectEditingListContacts: (arg1: State) => Contact[] =
  createSelector(getContacts, getEditingList, (contactMap, contactList) =>
    contactList.map((id) => (typeof id === 'string' ? contactMap[id] : id)),
  );

const getLists = (state: State) =>
  // @ts-expect-error - TS2339 - Property 'lists' does not exist on type 'ContactListsState'.
  state.contactLists.lists;
export const selectSortedContactLists: (state: State) => ContactList[] =
  createSelector(getLists, (lists) =>
    sortBy(
      Object.keys(lists).map((list) => lists[list]),
      'name',
    ),
  );

const getList = (state: State, listId: string) =>
  // @ts-expect-error - TS2339 - Property 'lists' does not exist on type 'ContactListsState'.
  state.contactLists.lists[listId];
export const selectContactsForList: (arg1: State, arg2: string) => Contact[] =
  createSelector(
    getList,
    getContacts,
    // NOTE (kyle): it's unlikely (but possible) that contacts in a group will not
    // exist in the global store. we filter undefined values out here.
    (list, contacts) => list.contacts.map((id) => contacts[id]).filter(Boolean),
  );

export const selectNonOptOutContactsForList: (
  arg1: State,
  arg2: string,
) => Contact[] = createSelector(selectContactsForList, (contacts) =>
  // @ts-expect-error - TS2339 - Property 'optedOut' does not exist on type 'Contact'.
  contacts.filter(({optedOut}) => !optedOut),
);
export const selectOptOutContactsForList: (
  arg1: State,
  arg2: string,
) => Contact[] = createSelector(selectContactsForList, (contacts) =>
  // @ts-expect-error - TS2339 - Property 'optedOut' does not exist on type 'Contact'.
  contacts.filter(({optedOut}) => optedOut),
);

// TODO (kyle): this is probably inefficient.
export const selectListsForContact: (
  arg1: State,
  arg2: string,
) => ContactList[] = createSelector(useParam, getLists, (contactId, lists) =>
  Object.keys(lists)
    .map((list) => lists[list])
    .filter((list) => list.contacts.includes(contactId)),
);

const normalizeString = (str: null | string) =>
  (str || '').replace(/\W/g, '').toLowerCase();

export const filterContactsForSearch = (
  contacts: Contact[],
  searchTerm: string,
): Contact[] => {
  searchTerm = normalizeString(searchTerm);

  return !searchTerm
    ? []
    : contacts
        .filter((contact) => {
          checkForContactPhoneError(contact);

          // TODO (kyle): fix this case when we move all contacts to a list of phone numbers
          const phone = contact.phoneNumbers[0];
          const matchesPhone = phone && phone.value.includes(searchTerm);

          let {firstName = '', lastName = ''} = contact;
          firstName = normalizeString(firstName);
          lastName = normalizeString(lastName);

          const matchesName = `${firstName}${lastName}`.includes(searchTerm);

          return matchesPhone || matchesName;
          // NOTE (kyle): we do this to make rendering easier
        })
        // TODO (kyle): copying this object may mess with memoization down the line
        // we should see about removing this.
        .map((contact) => ({
          ...contact,
          phone: contact.phoneNumbers[0] && contact.phoneNumbers[0].value,
        }));
};

export const selectContactsForSearch: (arg1: State, arg2: string) => Contact[] =
  createSelector(selectContactsAsArray, useParam, filterContactsForSearch);

// TODO (kyle): remove this later. it's temporary.
// @ts-expect-error - TS7006 - Parameter 'contact' implicitly has an 'any' type.
function checkForContactPhoneError(contact) {
  if (!contact.phoneNumbers) {
    captureSentryMessage('Contact is missing `phoneNumbers`', {
      level: 'error',
      extra: {
        contact,
      },
    });

    // NOTE (kyle): we intentionally mutate the contact here to avoid
    // duplicating this error.
    contact.phoneNumbers = [];
  }
}
